import { useRef, useState } from "react";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { Camera } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { Snackbar } from "../Snackbar";
import { DEFAULT_SNACKBAR, ERROR_PROFILE_PICTURE, SUCCESS_PROFILE_PICTURE } from "../../utils/snackbar";
import { THEME_SEVERITY } from "../../utils/theme";
import avatarPlaceholder from "../Sidebar/assets/person-circle.svg";

export const UserProfileModal = ({ open, onClose }) => {
  const { user, UpdateProfilePicture, dictionary } = useAuth();
  const theme = useTheme();
  const inputRef = useRef(null);
  const feedbackTimeoutRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState(DEFAULT_SNACKBAR);

  const showFeedback = (feedback) => {
    clearTimeout(feedbackTimeoutRef.current);
    setSnackbar(feedback);
    feedbackTimeoutRef.current = setTimeout(() => setSnackbar(DEFAULT_SNACKBAR), 3000);
  };

  const handleAvatarClick = () => {
    if (!uploading) inputRef.current?.click();
  };

  const handleClose = () => {
    if (uploading) return;
    clearTimeout(feedbackTimeoutRef.current);
    setSnackbar(DEFAULT_SNACKBAR);
    onClose();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showFeedback({ visible: true, message: "Selecciona un archivo de imagen.", severity: THEME_SEVERITY.ERROR });
      return;
    }
    setUploading(true);
    clearTimeout(feedbackTimeoutRef.current);
    setSnackbar(DEFAULT_SNACKBAR);
    try {
      const result = await UpdateProfilePicture(file);
      showFeedback(result.refreshed
        ? SUCCESS_PROFILE_PICTURE
        : {
            visible: true,
            message: "La foto se actualizó, pero no se pudo refrescar el perfil. Vuelve a abrirlo más tarde.",
            severity: THEME_SEVERITY.WARNING,
          });
    } catch (error) {
      showFeedback(ERROR_PROFILE_PICTURE);
    } finally {
      setUploading(false);
    }
  };

  if (!user) return null;

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="user-profile-title"
      PaperProps={{
        style: {
          borderRadius: "16px",
          backgroundColor: theme.colors.backgroundColorNavBar,
          color: theme.colors.textColorNavbar,
          padding: "2rem",
          zIndex: "1000",
        },
      }}
    >
      <IconButton
        aria-label="Cerrar perfil"
        onClick={handleClose}
        disabled={uploading}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.colors.textColorNavbar,
          zIndex: 1,
        }}
      >
        <CloseIcon sx={{ fontSize: 30 }} />
      </IconButton>
      <DialogContent sx={{ padding: 0 }}>
        <div className="flex flex-col gap-12 max-w-full">
          <section className="flex flex-col gap-6 tablet:flex-row tablet:items-center">
            <div className="relative h-[96px] w-[96px] shrink-0 tablet:h-[128px] tablet:w-[128px]">
              <button
                type="button"
                aria-label="Cambiar foto de perfil"
                title="Cambiar foto de perfil"
                onClick={handleAvatarClick}
                disabled={uploading}
                className="group relative overflow-hidden rounded-full w-full h-full p-0 border-0 cursor-pointer"
              >
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-500/50 opacity-0 backdrop-blur-[1px] transition-opacity duration-300 group-hover:!opacity-100 group-focus-visible:!opacity-100">
                  <EditRoundedIcon sx={{ fontSize: 30 }} className="text-[#272727]" />
                </div>
                <img
                  src={user.profileimageurl || avatarPlaceholder}
                  alt={user.fullname}
                  className={`w-full h-full object-cover ${uploading ? "opacity-50" : ""}`}
                />
                {uploading && (
                  <div className="absolute inset-0 z-20 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
                  </div>
                )}
              </button>
              <input
                ref={inputRef}
                type="file"
                className="hidden"
                accept="image/*"
                aria-label="Seleccionar foto de perfil"
                onChange={handleFileChange}
              />
            </div>
            <div className="flex min-w-0 flex-col items-start gap-2 tablet:pr-10">
              <h3 id="user-profile-title" className="max-w-full truncate text-[30px] font-semibold">
                {user.fullname}
              </h3>
              <button
                type="button"
                onClick={handleAvatarClick}
                disabled={uploading}
                className="inline-flex items-center gap-2 rounded-md py-1 font-medium underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Camera className="h-5 w-5" aria-hidden="true" />
                Cambiar foto
              </button>
            </div>
          </section>
          <section className="flex flex-col gap-4">
            <h4 className="text-[24px] font-semibold">
              {dictionary?.UserProfile?.personalData || "Datos personales"}
            </h4>
            <div className="grid grid-flow-row">
              <div className="flex items-center justify-between py-3 border-b-2" style={{ borderColor: theme.colors.textColorNavbar + "66" }}>
                <div className="flex flex-col">
                  <span className="text-[16px] font-medium">{dictionary?.UserProfile?.name || "Nombre"}</span>
                  <span className="text-[14px]">{user.fullname}</span>
                </div>
              </div>
              <div className="flex items-center justify-between py-3">
                <div className="flex flex-col">
                  <span className="text-[16px] font-medium">{dictionary?.UserProfile?.emailLabel || "Correo electrónico"}</span>
                  <span className="text-[14px]">{user.email}</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
    <div style={{ position: "fixed", inset: 0, zIndex: 1400, pointerEvents: "none" }}>
      <Snackbar message={snackbar.message} severity={snackbar.severity} visible={snackbar.visible} />
    </div>
    </>
  );
};
